import { Fragment, useMemo, useState } from "react";
import { InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import budgetService, { DEFAULT_BUDGET_YEAR } from "api/budgetService";
import BudgetYearSelect from "components/Budget/BudgetYearSelect";
import { IBudgetExpensesReportItem } from "dto/Budget/IBudgetExpensesReport";
import styles from "./styles.module.scss";

type GKey =
  | "G5"
  | "G6"
  | "G7"
  | "G8"
  | "G9"
  | "G9A"
  | "G10"
  | "G11"
  | "G12"
  | "G13"
  | "G14"
  | "G15"
  | "G16"
  | "G17"
  | "G17A"
  | "G18"
  | "G19";

type RowValues = Record<GKey, number>;

const VALUE_ORDER: GKey[] = [
  "G6",
  "G7",
  "G8",
  "G9",
  "G9A",
  "G10",
  "G11",
  "G12",
  "G13",
  "G14",
  "G15",
  "G16",
  "G17",
  "G17A",
  "G18",
  "G19",
];

const META: Record<GKey, { num: number; label: string }> = {
  G5: { num: 5, label: "" },
  G6: { num: 6, label: "" },
  G7: { num: 7, label: "" },
  G8: { num: 8, label: "wynagrodzenia i składki od nich naliczane" },
  G9: { num: 9, label: "wydatki związane z realizacją ich statutowych zadań" },
  G9A: { num: 10, label: "" },
  G10: { num: 11, label: "dotacje na zadania bieżące" },
  G11: { num: 12, label: "świadczenia na rzecz osób fizycznych" },
  G12: {
    num: 13,
    label:
      "wydatki na programy finansowane z udziałem środków, o których mowa w art. 5 ust. 1 pkt 2 i 3",
  },
  G13: { num: 14, label: "wypłaty z tytułu poręczeń i gwarancji" },
  G14: { num: 15, label: "obsługa długu" },
  G15: { num: 16, label: "" },
  G16: { num: 17, label: "" },
  G17: {
    num: 18,
    label:
      "w tym: na programy finansowane z udziałem środków, o których mowa w art. 5 ust. 1 pkt 2 i 3",
  },
  G17A: { num: 19, label: "" },
  G18: { num: 20, label: "zakup i objęcie akcji i udziałów" },
  G19: { num: 21, label: "wniesienie wkładów do spółek prawa handlowego" },
};

const formatNumber = (value: number) =>
  new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const buildValues = (items: IBudgetExpensesReportItem[]): RowValues => {
  const sum = (...symbols: IBudgetExpensesReportItem["GroupSymbol"][]) =>
    items
      .filter((i) => symbols.includes(i.GroupSymbol))
      .reduce((s, i) => s + i.Amount, 0);

  const G8 = sum("B1WIP");
  const G9 = sum("B1PZB");
  const G10 = sum("B2DOT");
  const G11 = sum("B3SOF");
  const G12 = sum("B4SUZ");
  const G13 = sum("B5PGW");
  const G14 = sum("B6DLG");
  const G16 = sum("M1IWL", "M1IUZ");
  const G17 = sum("M1IUZ");
  const G18 = sum("M2ZAU");
  const G19 = sum("M3WSP");

  const G7 = G8 + G9;
  const G9A = G10 + G11 + G12 + G13 + G14;
  const G6 = G7 + G9A;
  const G17A = G18 + G19;
  const G15 = G16 + G17A;
  const G5 = G6 + G15;

  return {
    G5,
    G6,
    G7,
    G8,
    G9,
    G9A,
    G10,
    G11,
    G12,
    G13,
    G14,
    G15,
    G16,
    G17,
    G17A,
    G18,
    G19,
  };
};

interface ChapterRow {
  Symbol: string;
  Name: string;
  values: RowValues;
}

interface DivisionRow {
  Symbol: string;
  Name: string;
  values: RowValues;
  chapters: ChapterRow[];
}

const ExpensesReport = () => {
  const [year, setYear] = useState(DEFAULT_BUDGET_YEAR);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({
    G6: true,
    G7: true,
    G15: true,
    G16: true,
    G18: true,
    G9A: true,
    G17A: true,
  });

  const { data: report = [] } = budgetService.useGetExpensesReport(year);

  const toggle = (key: keyof typeof expanded) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const vis = (key: GKey): boolean => {
    switch (key) {
      case "G5":
      case "G6":
      case "G15":
        return true;
      case "G7":
        return expanded.G6;
      case "G8":
      case "G9":
        return expanded.G6 && expanded.G7;
      case "G9A":
        return expanded.G6;
      case "G10":
      case "G11":
      case "G12":
      case "G13":
      case "G14":
        return expanded.G6 && expanded.G9A;
      case "G16":
        return expanded.G15;
      case "G17":
        return expanded.G15 && expanded.G16;
      case "G17A":
        return expanded.G15;
      case "G18":
      case "G19":
        return expanded.G15 && expanded.G17A;
    }
  };

  const visibleKeys = VALUE_ORDER.filter(vis);
  const countVisible = (keys: GKey[]) => keys.filter(vis).length;

  const divisions = useMemo<DivisionRow[]>(() => {
    const q = search.trim().toLowerCase();
    const byDivision = new Map<string, IBudgetExpensesReportItem[]>();
    report.forEach((item) => {
      const list = byDivision.get(item.DivisionSymbol) ?? [];
      list.push(item);
      byDivision.set(item.DivisionSymbol, list);
    });

    const result: DivisionRow[] = [];
    byDivision.forEach((divItems) => {
      const byChapter = new Map<string, IBudgetExpensesReportItem[]>();
      divItems.forEach((item) => {
        const list = byChapter.get(item.ChapterSymbol) ?? [];
        list.push(item);
        byChapter.set(item.ChapterSymbol, list);
      });

      const chapters: ChapterRow[] = [];
      byChapter.forEach((chItems) => {
        const symbol = chItems[0].ChapterSymbol;
        const name = chItems[0].ChapterName;
        if (q && !`${symbol} ${name}`.toLowerCase().includes(q)) return;
        chapters.push({ Symbol: symbol, Name: name, values: buildValues(chItems) });
      });

      if (chapters.length === 0) return;
      chapters.sort((a, b) => a.Symbol.localeCompare(b.Symbol));

      const visibleItems = divItems.filter((i) =>
        chapters.some((c) => c.Symbol === i.ChapterSymbol),
      );

      result.push({
        Symbol: divItems[0].DivisionSymbol,
        Name: divItems[0].DivisionName,
        values: buildValues(visibleItems),
        chapters,
      });
    });

    result.sort((a, b) => a.Symbol.localeCompare(b.Symbol));
    return result;
  }, [report, search]);

  const arrow = (key: keyof typeof expanded) => (
    <button
      className={styles.arrow}
      onClick={() => toggle(key)}
      type="button"
      aria-label={expanded[key] ? "Zwiń" : "Rozwiń"}
    >
      {expanded[key] ? "▾" : "▸"}
    </button>
  );

  const valueCells = (values: RowValues, cls: string) =>
    visibleKeys.map((key) => (
      <td key={key} className={`${styles.num} ${cls}`}>
        {formatNumber(values[key])}
      </td>
    ));

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className={styles.title}>Raport wydatków</span>
          <BudgetYearSelect value={year} onChange={setYear} />
        </div>
        <TextField
          size="small"
          placeholder="Szukaj na liście"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchBox}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th rowSpan={4} className={styles.nameHead}>
                Nazwa
              </th>
              <th rowSpan={4} className={styles.planHead}></th>
              <th colSpan={visibleKeys.length} className={styles.bandPlan}>
                Plan, z tego:
              </th>
            </tr>
            <tr>
              <th
                colSpan={countVisible([
                  "G6",
                  "G7",
                  "G8",
                  "G9",
                  "G9A",
                  "G10",
                  "G11",
                  "G12",
                  "G13",
                  "G14",
                ])}
                className={styles.band}
              >
                Wydatki bieżące, z tego:
              </th>
              {countVisible(["G15", "G16", "G17", "G17A", "G18", "G19"]) > 0 && (
                <th
                  colSpan={countVisible(["G15", "G16", "G17", "G17A", "G18", "G19"])}
                  className={styles.band}
                >
                  Wydatki majątkowe, z tego:
                </th>
              )}
            </tr>
            <tr>
              {/* Wydatki bieżące */}
              <th rowSpan={2} className={styles.agg}>
                {arrow("G6")}
              </th>
              {vis("G7") && (
                <th colSpan={countVisible(["G7", "G8", "G9"])} className={styles.band}>
                  wydatki jednostek budżetowych, z tego:
                </th>
              )}
              {vis("G9A") && (
                <th
                  colSpan={countVisible(["G9A", "G10", "G11", "G12", "G13", "G14"])}
                  className={styles.band}
                >
                  Inne wydatki bieżące, w tym:
                </th>
              )}
              {/* Wydatki majątkowe */}
              <th rowSpan={2} className={styles.agg}>
                {arrow("G15")}
              </th>
              {vis("G16") && (
                <th colSpan={countVisible(["G16", "G17"])} className={styles.band}>
                  inwestycje i zakupy inwestycyjne
                </th>
              )}
              {vis("G17A") && (
                <th
                  colSpan={countVisible(["G17A", "G18", "G19"])}
                  className={styles.band}
                >
                  Nabycie akcji i udziałów, w tym:
                </th>
              )}
            </tr>
            <tr>
              {vis("G7") && (
                <th className={styles.agg}>{arrow("G7")}</th>
              )}
              {vis("G8") && <th className={styles.leafHead}>{META.G8.label}</th>}
              {vis("G9") && <th className={styles.leafHead}>{META.G9.label}</th>}
              {vis("G9A") && (
                <th className={styles.agg}>{arrow("G9A")}</th>
              )}
              {vis("G10") && <th className={styles.leafHead}>{META.G10.label}</th>}
              {vis("G11") && <th className={styles.leafHead}>{META.G11.label}</th>}
              {vis("G12") && <th className={styles.leafHead}>{META.G12.label}</th>}
              {vis("G13") && <th className={styles.leafHead}>{META.G13.label}</th>}
              {vis("G14") && <th className={styles.leafHead}>{META.G14.label}</th>}
              {vis("G16") && (
                <th className={styles.agg}>{arrow("G16")}</th>
              )}
              {vis("G17") && <th className={styles.leafHead}>{META.G17.label}</th>}
              {vis("G17A") && (
                <th className={styles.agg}>{arrow("G17A")}</th>
              )}
              {vis("G18") && <th className={styles.leafHead}>{META.G18.label}</th>}
              {vis("G19") && <th className={styles.leafHead}>{META.G19.label}</th>}
            </tr>
            <tr className={styles.numRow}>
              <th>4</th>
              <th>5</th>
              {visibleKeys.map((key) => (
                <th key={key}>{META[key].num}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {divisions.map((division) => (
              <Fragment key={division.Symbol}>
                <tr className={styles.divisionRow}>
                  <td className={styles.nameCell}>{division.Name}</td>
                  <td className={`${styles.num} ${styles.planCell}`}>
                    {formatNumber(division.values.G5)}
                  </td>
                  {valueCells(division.values, styles.divisionNum)}
                </tr>
                {division.chapters.map((chapter) => (
                  <tr
                    key={`${division.Symbol}-${chapter.Symbol}`}
                    className={styles.chapterRow}
                  >
                    <td className={styles.nameCell}>
                      <span className={styles.chapterSymbol}>{chapter.Symbol}</span>
                      <span className={styles.chapterName}>{chapter.Name}</span>
                    </td>
                    <td className={`${styles.num} ${styles.planCell}`}>
                      {formatNumber(chapter.values.G5)}
                    </td>
                    {valueCells(chapter.values, "")}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExpensesReport;
